import React from "react";

// ─────────────────────────────────────────────────────────────────────────
// MedidaConMm — muestra un texto con medidas en pulgadas y, al lado de cada una, su
// equivalente en milímetros de forma discreta: `SAE 4140 Ø3" × 170` → SAE 4140 Ø3" (76,2 mm) × 170.
//
// Pedido de Maxi (21-oct-2026): al comparar lo que se pidió cotizar con lo que el proveedor
// cotizó, quien revisa tiene que poder seguir la cadena pieza terminada (mm) → pedido (pulgadas
// o mm) → cotizado (pulgadas o mm) sin convertir de cabeza. Entiende 3", 2.1/4", 2 1/4", 2½",
// 2.5" y 2,5"; las medidas que ya están en mm se dejan como están.
//
// Cuidados (revisión 21-oct): una comilla que cierra un texto entre comillas (`Pieza "BUJE 40"`)
// NO es una medida; un grado pegado a la fracción (`SAE 1045 1/2"`) no se lee como 1045½";
// `3"x 2"` convierte las dos. Sin lookbehind (Safari viejo no lo soporta): el contexto se mira a mano.
// ─────────────────────────────────────────────────────────────────────────

const FRACCION = { "½": 0.5, "¼": 0.25, "¾": 0.75, "⅛": 0.125, "⅜": 0.375, "⅝": 0.625, "⅞": 0.875, "⅓": 1 / 3, "⅔": 2 / 3 };

// entero (1-2 dígitos) + fracción (2.1/4, 2 1/4, 2-1/4, 2½) · solo fracción (1/4, ½) · decimal (2.5, 2,5)
// · entero (3); seguido del signo de pulgadas: " ” '' o la palabra pulg. Después puede venir ×, x, *, espacio…
const RE_PULGADAS = /(\d{1,2})?(?:(?:[.\s-])?(\d{1,2})\/(\d{1,2})|\s?([½¼¾⅛⅜⅝⅞⅓⅔]))?(?:([.,])(\d{1,2}))?\s?(?:"|”|''|pulg\.?|pulgadas?)(?![a-wyzáéíóúA-WYZ])/g;
const MAX_PULGADAS = 24; // barras comerciales hasta 20"; más que eso no es una medida de barra

const valorDe = (m, sinEntero = false) => {
  const [, entero0, num, den, uni, , dec] = m;
  const entero = sinEntero ? null : entero0;
  if (num && den && Number(den)) return (Number(entero) || 0) + Number(num) / Number(den);
  if (uni) return (Number(entero) || 0) + FRACCION[uni];
  if (dec != null) return entero == null && sinEntero ? null : Number(`${entero || 0}.${dec}`);
  if (entero) return Number(entero);
  return null;
};

/** Convierte una medida en pulgadas (texto) a mm, o null si no la entiende. */
export const pulgadasAMm = (texto) => {
  const t = trozosConMm(texto).find(x => x.mm != null);
  return t ? t.mm : null;
};

export const fmtMm = (mm) => `${mm.toLocaleString("es-CL", { minimumFractionDigits: 0, maximumFractionDigits: 1 })} mm`;

const esDigitoOSep = (c) => /[\d.,/]/.test(c || "");

/**
 * Divide el texto en trozos [{ texto, mm }]: `mm` viene en los trozos que son una medida en pulgadas.
 * Función pura (sirve para renderizar y para probar).
 */
export const trozosConMm = (texto) => {
  const s = String(texto || "");
  const out = [];
  let ultimo = 0;
  let comillaAbierta = false; // comillas de texto (no de medida) vistas hasta aquí
  const re = new RegExp(RE_PULGADAS.source, "g");
  let m;
  while ((m = re.exec(s)) !== null) {
    if (m[0].length === 0) { re.lastIndex++; continue; }
    const inicio = m.index;
    const previo = s[inicio - 1] || "";
    const texto = m[0];
    // comillas "de texto" entre el trozo anterior y esta medida
    for (const c of s.slice(ultimo, inicio)) if (c === '"') comillaAbierta = !comillaAbierta;
    let v;
    if (esDigitoOSep(previo)) {
      // El entero capturado es la cola de un número más largo ("1045 1/2"" → el 45 es del grado):
      // vale solo la fracción si va separada por espacio; "123.5"" no es una medida.
      const k = texto.search(/\s/);
      if (m[1] && k === m[1].length) {
        const sub = new RegExp(RE_PULGADAS.source).exec(texto.slice(k + 1));
        v = sub && !sub[1] ? valorDe(sub) : null;
      } else {
        v = null;
      }
    } else {
      v = valorDe(m);
    }
    const esMedida = v != null && v > 0 && v <= MAX_PULGADAS && /^\s*(\d|[½¼¾⅛⅜⅝⅞⅓⅔])/.test(texto) && !comillaAbierta;
    if (!esMedida) {
      // la comilla de este trozo era de texto (cierra o abre una cita)
      if (texto.includes('"')) comillaAbierta = !comillaAbierta;
      continue;
    }
    // Si el texto ya trae el equivalente entre paréntesis (`Ø6" (152,4)`), no se repite.
    const yaLoTrae = /^\s*\(\s*[\d.,]+\s*(?:mm)?\s*\)/i.test(s.slice(inicio + texto.length));
    if (inicio > ultimo) out.push({ texto: s.slice(ultimo, inicio) });
    // Redondeo a 0,1 mm sin el error binario del flotante: 1,75" × 25,4 = 44,45 → 44,5 (no 44,4).
    out.push(yaLoTrae ? { texto } : { texto, mm: Math.round(v * 254 + 1e-9) / 10 });
    ultimo = inicio + texto.length;
  }
  if (ultimo < s.length) out.push({ texto: s.slice(ultimo) });
  return out;
};

const estiloMm = { fontSize: "0.85em", color: "#8a8a8a", fontWeight: 400, whiteSpace: "nowrap" };

export default function MedidaConMm({ texto, children, style }) {
  const trozos = trozosConMm(texto != null ? texto : children);
  if (!trozos.some(t => t.mm != null)) return <>{texto != null ? texto : children}</>;
  return (
    <span style={style}>
      {trozos.map((t, i) => t.mm != null
        ? <React.Fragment key={i}>{t.texto}<span style={estiloMm} title={`${t.texto} = ${fmtMm(t.mm)}`}> ({fmtMm(t.mm)})</span></React.Fragment>
        : <React.Fragment key={i}>{t.texto}</React.Fragment>)}
    </span>
  );
}
